import { useEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react';

interface Options {
  value: string;
  /** Called only with a changed value, and a non-empty one unless `allowEmpty`. */
  onCommit: (value: string) => void;
  /** Names the field for screen readers, e.g. "Folder name". */
  label: string;
  /** Commits an emptied field too, for a name that falls back to a default when cleared. */
  allowEmpty?: boolean;
}

export interface EditableName {
  isEditing: boolean;
  /** Opens the field, seeded with the current value. */
  start: () => void;
  /** Spread onto the `input` that replaces the text. */
  inputProps: {
    autoFocus: true;
    value: string;
    'aria-label': string;
    onChange: (event: { target: { value: string } }) => void;
    onBlur: () => void;
    onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  };
  /** Give this to the control that opens the field, so focus can return to it. */
  editControlRef: RefObject<HTMLElement | null>;
  /**
   * True only while the hook hands focus back to `editControlRef` as the field closes. Focus
   * events fire inside `focus()`, so a focus handler on that control can tell the hand-back from
   * a Tab or a click, which `relatedTarget` cannot: a first Tab from <body> has none either.
   */
  isReturningFocus: () => boolean;
}

/**
 * Rename-in-place, without the markup.
 *
 * The caller owns how the name reads — a breadcrumb crumb and a page heading
 * carry different type and different ARIA — while the parts that are easy to
 * get wrong live here: returning focus after the field unmounts, and keeping
 * Enter and Escape from reaching whatever the name is rendered inside.
 *
 * `draft === null` is the single source of truth for "not editing", which also
 * makes commit idempotent: once it clears the draft, the blur that follows
 * Enter reads an empty draft and does nothing.
 */
export function useEditableName(options: Options): EditableName {
  const [draft, setDraft] = useState<string | null>(null);
  const [restoreFocus, setRestoreFocus] = useState(false);
  const editControlRef = useRef<HTMLElement>(null);
  const returningFocusRef = useRef(false);

  // Leaving edit mode destroys the focused input, which drops focus to <body>
  // and sends the next Tab back to the top of the page. Keyboard exits hand it
  // to the control the field was opened from; a blur exit does not, because
  // focus is already on its way somewhere the user chose.
  useEffect(() => {
    if (!restoreFocus) return;
    returningFocusRef.current = true;
    editControlRef.current?.focus();
    returningFocusRef.current = false;
    setRestoreFocus(false);
  }, [restoreFocus]);

  const commit = () => {
    const next = (draft ?? '').trim();
    // An empty name is rejected rather than saved, unless the caller has a default to fall back
    // to: the previous one stands.
    if ((next || options.allowEmpty) && next !== options.value) {
      options.onCommit(next);
    }
    setDraft(null);
  };

  return {
    isEditing: draft !== null,
    start: () => setDraft(options.value),
    editControlRef,
    isReturningFocus: () => returningFocusRef.current,
    inputProps: {
      autoFocus: true,
      value: draft ?? '',
      'aria-label': options.label,
      onChange: (event) => setDraft(event.target.value),
      onBlur: commit,
      onKeyDown: (event) => {
        if (event.key === 'Enter') {
          // Without this the same keystroke also submits an enclosing form.
          event.preventDefault();
          setRestoreFocus(true);
          event.currentTarget.blur();
        } else if (event.key === 'Escape') {
          // Otherwise the key keeps travelling and closes any Dialog or
          // Popover the name happens to be rendered inside.
          event.stopPropagation();
          setDraft(null);
          setRestoreFocus(true);
        }
      },
    },
  };
}
